import React from "react";
import { format } from "date-fns";
import { Card } from "@/components/ui";
import { ReviewRating, ReplyStatus, formatRelativeTime } from "@/components/reviews";
import { Review, type ReviewType } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import { SocialSvg } from "@/components";
import { SocialNetworksId } from "@/interfaces";

type CoachReviewCardProps = {
  review: Review;
  isSelected?: boolean;
  grouped?: boolean;
  primaryRatingType?: ReviewType | null;
  onClick?: () => void;
};

export function CoachReviewCard({
  review,
  isSelected = false,
  grouped = false,
  primaryRatingType = null,
  onClick,
}: CoachReviewCardProps) {
  const player = review.player;
  const publishedAt = review.publishedAt ? new Date(review.publishedAt) : null;
  const publishedTitle = publishedAt ? format(publishedAt, "MMM d, yyyy h:mm a") : undefined;
  const publishedLabel = publishedAt ? formatRelativeTime(review.publishedAt) : "";

  const content = (
    <div className="flex flex-col gap-1.5">
      <div className="min-w-0 grid grid-cols-[auto_1fr_auto] gap-x-3 gap-y-1 items-center">
        <div className="row-span-2 flex shrink-0 items-center self-stretch">
          <SocialSvg networkId={review.sourceSystem as SocialNetworksId} variant="icon" size="sm" />
        </div>
        <span
          className={cn(
            "font-sans font-semibold leading-tight truncate",
            isSelected ? "text-foreground" : undefined,
            !review.displayName && "italic text-muted-foreground"
          )}
        >
          {review.displayName || "Anonymous"}
        </span>
        <ReviewRating
          review={review}
          size="sm"
          fallback={null}
          primaryRatingType={primaryRatingType}
          className="justify-self-end"
        />
        <div className="min-w-0 flex gap-1.5 items-center">
          <span
            className={cn(
              "font-sans text-xs font-medium truncate",
              isSelected ? "text-foreground/70" : "text-muted-foreground",
              !player?.fullName && "italic"
            )}
          >
            {player ? player.fullName || "No Name" : "Unassigned"}
          </span>
          <span className="@min-[24rem]:hidden inline-flex shrink-0">
            <ReplyStatus review={review} variant="icon" resolveInternalStatus={true} />
          </span>
          <span className="hidden @min-[24rem]:inline-flex shrink-0">
            <ReplyStatus review={review} variant="badge" resolveInternalStatus={true} />
          </span>
        </div>
        <span
          title={publishedTitle}
          className={cn(
            "font-sans text-xs font-medium justify-self-end",
            isSelected ? "text-foreground/70" : "text-muted-foreground"
          )}
        >
          {publishedLabel}
        </span>
      </div>

      {review.content ? (
        <p
          className={cn(
            "m-0 font-sans text-sm leading-relaxed line-clamp-2 text-muted-foreground",
            isSelected && "text-foreground/80"
          )}
        >
          {review.content}
        </p>
      ) : null}
    </div>
  );

  return (
    <Card
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={cn(
        "group @container relative font-sans flex flex-col gap-1.5 py-3 pr-4 @min-[24rem]:py-4 @min-[24rem]:pr-5 border transition-colors duration-200 ring-0",
        onClick && "cursor-pointer",
        grouped
          ? "border-b-0 last:border-b rounded-none first:rounded-t-2xl last:rounded-b-2xl shadow-none"
          : cn("rounded-xl shadow-sm", onClick && "hover:shadow-md"),
        isSelected
          ? "bg-muted/50 border-slate-600 border-l-4 pl-[13px] @min-[24rem]:pl-[17px]"
          : cn(
              "bg-white border-slate-300 pl-4 @min-[24rem]:pl-5",
              onClick && "hover:bg-slate-50/20 hover:border-l-4 hover:pl-[13px] @min-[24rem]:hover:pl-[17px]"
            )
      )}
    >
      {content}
    </Card>
  );
}
